/**
 * Page models: everything a public page needs, already localized and formatted.
 * Web components receive these, never raw content documents.
 *
 * Internal-only fields (technical.internal, installation.technicalInternal)
 * are intentionally never read here.
 */
import type { Asset, ExhibitionDocument, Installation, WorkDocument } from "@yushi-portfolio/content-schema";
import {
  orderExhibitionsChronologically,
  orderInstallations,
  orderMedia,
  orderWorksChronologically,
  primaryMedia,
  worksByExhibition,
  type ContentSet,
} from "@yushi-portfolio/domain";
import { formatDateRange, formatDuration, formatWorkYear, yearOf } from "./format";
import { label, pickNonEmpty, pickText, type Locale } from "./locale";
import { renderMarkdown } from "./markdown";
import { toMediaModel, toMediaModels, type ImageModel, type MediaContext, type MediaModel } from "./media";

export type PresentationOptions = {
  locale: Locale;
  /** See MediaContext.mediaBaseUrl. */
  mediaBaseUrl: string;
};

export type PresentationContext = MediaContext & {
  exhibitions: ReadonlyMap<string, ExhibitionDocument>;
};

export function createPresentationContext(content: ContentSet, options: PresentationOptions): PresentationContext {
  return {
    locale: options.locale,
    mediaBaseUrl: options.mediaBaseUrl.replace(/\/+$/, ""),
    assets: new Map<string, Asset>(content.assets.map((a) => [a.id, a])),
    exhibitions: new Map(content.exhibitions.map((e) => [e.id, e])),
  };
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export const workPath = (slug: string) => `/works/${slug}/`;

// ---------------------------------------------------------------------------
// Work page
// ---------------------------------------------------------------------------

export type MetadataItem = { label: string; value: string };

export type InstallationViewModel = {
  title?: string;
  venue?: string;
  yearLabel?: string;
  dimensions?: string;
  descriptionHtml: string;
  media: MediaModel[];
};

export type CreditModel = { role: string; name: string };

export type WorkPageModel = {
  slug: string;
  path: string;
  title: string;
  yearLabel: string;
  typeLabel: string;
  metadata: MetadataItem[];
  conceptHtml: string;
  /** The media item that represents the work, if any. */
  primaryMedia?: MediaModel;
  /** Remaining work media in display order. */
  media: MediaModel[];
  installations: InstallationViewModel[];
  credits: CreditModel[];
  /** Plain-text summary for <meta name="description">. */
  description?: string;
};

function venueLabel(installation: Installation, exhibition: ExhibitionDocument | undefined, locale: Locale) {
  const override = pickNonEmpty(installation.venueOverride, locale);
  if (override) return override;
  if (!exhibition) return undefined;
  const parts = [exhibition.venue, exhibition.city, exhibition.country].filter(Boolean);
  return parts.length ? parts.join(", ") : undefined;
}

function toInstallationView(installation: Installation, workTitle: string, ctx: PresentationContext): InstallationViewModel {
  const exhibition = installation.exhibitionId ? ctx.exhibitions.get(installation.exhibitionId) : undefined;
  const title = pickNonEmpty(installation.title, ctx.locale) ?? pickNonEmpty(exhibition?.title, ctx.locale);
  const year = installation.year ?? yearOf(exhibition?.startDate);
  return {
    title,
    venue: venueLabel(installation, exhibition, ctx.locale),
    yearLabel: year !== undefined ? String(year) : undefined,
    dimensions: pickNonEmpty(installation.dimensions, ctx.locale),
    descriptionHtml: renderMarkdown(pickText(installation.description, ctx.locale)),
    media: toMediaModels(orderMedia(installation.media), title ? `${workTitle}, ${title}` : workTitle, ctx),
  };
}

function workMetadata(work: WorkDocument, locale: Locale): MetadataItem[] {
  const items: (MetadataItem | undefined)[] = [
    textItem("medium", work.medium),
    textItem("dimensions", work.dimensions),
    work.durationSeconds !== undefined
      ? { label: label("duration", locale), value: formatDuration(work.durationSeconds, locale) }
      : undefined,
    textItem("technical", work.technical?.public),
  ];
  return items.filter((item): item is MetadataItem => item !== undefined);

  function textItem(key: "medium" | "dimensions" | "technical", text: WorkDocument["medium"]) {
    const value = pickNonEmpty(text, locale);
    return value ? { label: label(key, locale), value } : undefined;
  }
}

function plainSummary(markdown: string | undefined, maxLength = 160): string | undefined {
  if (!markdown) return undefined;
  const text = markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return undefined;
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

export function createWorkPageModel(work: WorkDocument, ctx: PresentationContext): WorkPageModel {
  const title = pickText(work.title, ctx.locale);
  const primary = primaryMedia(work);
  const rest = orderMedia(work.media).filter((m) => m !== primary);
  const concept = pickText(work.concept, ctx.locale);

  return {
    slug: work.slug,
    path: workPath(work.slug),
    title,
    yearLabel: formatWorkYear(work.date),
    typeLabel: label(`type.${work.type}`, ctx.locale),
    metadata: workMetadata(work, ctx.locale),
    conceptHtml: renderMarkdown(concept),
    primaryMedia: primary ? toMediaModel(primary, title, ctx) : undefined,
    media: toMediaModels(rest, title, ctx),
    installations: orderInstallations(work.installations).map((i) => toInstallationView(i, title, ctx)),
    credits: work.credits.map((c) => ({ role: pickText(c.role, ctx.locale), name: pickText(c.name, ctx.locale) })),
    description: plainSummary(concept),
  };
}

// ---------------------------------------------------------------------------
// Work index
// ---------------------------------------------------------------------------

export type WorkListItemModel = {
  path: string;
  title: string;
  yearLabel: string;
  typeLabel: string;
  thumbnail?: ImageModel;
};

/** First image in the work's own media, preferring the featured item. */
function thumbnailOf(work: WorkDocument, title: string, ctx: PresentationContext): ImageModel | undefined {
  const primary = primaryMedia(work);
  const candidates = [...(primary ? [primary] : []), ...orderMedia(work.media)];
  for (const media of candidates) {
    const model = toMediaModel(media, title, ctx);
    if (model?.type === "image") return model;
  }
  return undefined;
}

export function createWorkListModel(works: readonly WorkDocument[], ctx: PresentationContext): WorkListItemModel[] {
  return orderWorksChronologically(works).map((work) => {
    const title = pickText(work.title, ctx.locale);
    return {
      path: workPath(work.slug),
      title,
      yearLabel: formatWorkYear(work.date),
      typeLabel: label(`type.${work.type}`, ctx.locale),
      thumbnail: thumbnailOf(work, title, ctx),
    };
  });
}

// ---------------------------------------------------------------------------
// Exhibitions
// ---------------------------------------------------------------------------

export type ExhibitionListItemModel = {
  title: string;
  typeLabel: string;
  venue?: string;
  dateLabel?: string;
  url?: string;
  works: { title: string; path: string }[];
};

export function createExhibitionListModel(content: ContentSet, ctx: PresentationContext): ExhibitionListItemModel[] {
  const worksIndex = worksByExhibition(content.works);
  return orderExhibitionsChronologically(content.exhibitions).map((exhibition) => {
    const location = [exhibition.venue, exhibition.city, exhibition.country].filter(Boolean).join(", ");
    return {
      title: pickText(exhibition.title, ctx.locale),
      typeLabel: label(`exhibition.${exhibition.type}`, ctx.locale),
      venue: location || undefined,
      dateLabel: formatDateRange(exhibition.startDate, exhibition.endDate),
      url: exhibition.url,
      works: orderWorksChronologically(worksIndex.get(exhibition.id) ?? []).map((w) => ({
        title: pickText(w.title, ctx.locale),
        path: workPath(w.slug),
      })),
    };
  });
}
